import React, { useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import { asve } from '../orve';
import {
  getAssessmentContentPercentile,
  getAssessmentContentCutoffs,
  getAssessmentText,
  getCandidateExtraversionText,
  getCandidateHonestyHumilityText,
  getAssessmentContentBlank,
} from '../report-util';
import { useAuth } from '../../../../providers/auth-provider';
import { REPORT_TYPE } from '../../../../utils/constants';
import { STAFFING_ONSITE_OPENING, STAFFING_REMOTE_OPENING } from '../../../shared/filters/filter-common';

export const ONSITE_BREAKPOINTS = {
  conscientiousness: {
    breaks: [4.55, 4.1],
    scale: 5,
  },
  agreeableness: {
    breaks: [3.95, 3.55],
    scale: 5,
  },
  honestyHumility: {
    breaks: [3.85, 3.45],
    scale: 5,
  },
};
export const REMOTE_BREAKPOINTS = {
  conscientiousness: {
    breaks: [4.45, 3.95],
    scale: 5,
  },
  agreeableness: {
    breaks: [3.95, 3.55],
    scale: 5,
  },
  honestyHumility: {
    breaks: [3.95, 3.65],
    scale: 5,
  },
  emotionalControl: {
    breaks: [3.65, 2.95],
    scale: 5,
  },
};

function PersonalityAcumenResults({ candidate, reportType = REPORT_TYPE.coaching, remoteStaffing = false }) {
  const { isSelect } = useAuth();
  const [assessmentText, setAssessmentText] = useState({});
  const [staffingLocation, setStaffingLocation] = useState(STAFFING_ONSITE_OPENING);

  useEffect(() => {
    if (candidate) {
      setAssessmentText(asve(candidate));
      if (remoteStaffing) {
        setStaffingLocation(STAFFING_REMOTE_OPENING);
      }
    }
  }, [candidate]);

  return (
    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
      {candidate && (
        <>
          <Container className="candidate-profile-assessment-content">
            <Row className="justify-content-left w-50">
              <h3 className="pt-4 pb-1">PERSONALITY</h3>
              {reportType !== REPORT_TYPE.staffing && (
                <Stack direction="horizontal" gap={4}>
                  {getAssessmentContentPercentile('Overall Personality', candidate.percentiles.personality, reportType)}
                </Stack>
              )}
            </Row>
            <Row className="w-100 pb-2">
              {reportType === REPORT_TYPE.staffing
                ? getAssessmentText(assessmentText[`${staffingLocation}StaffingPersonality`])
                : getAssessmentText(assessmentText.personality)}
            </Row>
            <Row className="w-100 mx-auto justify-content-center pt-3">
              <Stack direction="horizontal" className="subscore-chart">
                <div className="w-25">
                  {reportType === REPORT_TYPE.staffing
                    ? getAssessmentContentBlank(
                        getCandidateHonestyHumilityText(isSelect()),
                        candidate.personality.scores.honestyHumility.overall,
                        remoteStaffing ? REMOTE_BREAKPOINTS.honestyHumility : ONSITE_BREAKPOINTS.honestyHumility,
                        reportType
                      )
                    : getAssessmentContentCutoffs(
                        getCandidateHonestyHumilityText(isSelect()),
                        candidate.personality.scores.honestyHumility.overall,
                        undefined,
                        reportType
                      )}
                </div>
                <div className="w-75">
                  {reportType === REPORT_TYPE.staffing
                    ? getAssessmentText(assessmentText[`${staffingLocation}StaffingHonestyHumility`])
                    : getAssessmentText(assessmentText.honestyHumility)}
                </div>
              </Stack>
              {reportType === REPORT_TYPE.staffing && (
                <Stack direction="horizontal" className="subscore-chart">
                  <div className="w-25">
                    {getAssessmentContentBlank(
                      'Agreeableness',
                      candidate.personality.scores.agreeableness.overall,
                      remoteStaffing ? REMOTE_BREAKPOINTS.agreeableness : ONSITE_BREAKPOINTS.agreeableness,
                      reportType
                    )}
                  </div>
                  <div className="w-75">
                    {getAssessmentText(assessmentText[`${staffingLocation}StaffingAgreeableness`])}
                  </div>
                </Stack>
              )}
              {reportType === REPORT_TYPE.staffing && remoteStaffing && (
                <Stack direction="horizontal" className="subscore-chart">
                  <div className="w-25">
                    {getAssessmentContentBlank(
                      'Emotionality',
                      candidate.personality.scores.emotionalControl.overall,
                      REMOTE_BREAKPOINTS.emotionalControl,
                      reportType
                    )}
                  </div>
                  <div className="w-75">
                    {reportType === REPORT_TYPE.staffing
                      ? getAssessmentText(assessmentText[`${staffingLocation}StaffingEmotionalControl`])
                      : getAssessmentText(assessmentText.emotionalControl)}
                  </div>
                </Stack>
              )}
              {reportType !== REPORT_TYPE.staffing && (
                <Stack direction="horizontal" className="subscore-chart">
                  <div className="w-25">
                    {getAssessmentContentCutoffs(
                      getCandidateExtraversionText(isSelect(), true),
                      candidate.personality.scores.extraversion.overall,
                      undefined,
                      reportType
                    )}
                  </div>
                  <div className="w-75">{getAssessmentText(assessmentText.extraversion)}</div>
                </Stack>
              )}
              <Stack direction="horizontal" className="subscore-chart">
                <div className="w-25">
                  {reportType === REPORT_TYPE.staffing
                    ? getAssessmentContentBlank(
                        'Conscientiousness',
                        candidate.personality.scores.conscientiousness.overall,
                        remoteStaffing ? REMOTE_BREAKPOINTS.conscientiousness : ONSITE_BREAKPOINTS.conscientiousness,
                        reportType
                      )
                    : getAssessmentContentCutoffs(
                        'Conscientiousness',
                        candidate.personality.scores.conscientiousness.overall,
                        undefined,
                        reportType
                      )}
                </div>
                <div className="w-75">
                  {reportType === REPORT_TYPE.staffing
                    ? getAssessmentText(assessmentText[`${staffingLocation}StaffingConscientiousness`])
                    : getAssessmentText(assessmentText.conscientiousness)}
                </div>
              </Stack>
            </Row>
            {reportType !== REPORT_TYPE.staffing && (
              <>
                <Row className="mt-2 justify-content-left w-50">
                  <h3 className="pt-4 pb-1">ACUMEN</h3>
                  <Stack direction="horizontal" gap={4}>
                    {getAssessmentContentPercentile('Overall Acumen', candidate.percentiles.acumen, reportType)}
                  </Stack>
                </Row>
                <Row className="w-100 pb-4">{getAssessmentText(assessmentText.acumen)}</Row>
              </>
            )}
          </Container>
        </>
      )}
    </Container>
  );
}

export default PersonalityAcumenResults;
